/**
 * Hierarchical query-key factories. Each entity exposes:
 *
 *   all         → ['entity']                       (invalidates lists + details + infinite)
 *   lists()     → ['entity', 'list']
 *   list(p)     → ['entity', 'list', p]
 *   infinite(p) → ['entity', 'infinite', p]
 *   details()   → ['entity', 'detail']
 *   detail(id,p)→ ['entity', 'detail', String(id), p]
 *   custom(k,p) → ['entity', 'custom', k, p]
 *
 * For company-scoped queries, include `companyId` inside the params object —
 * cache then automatically splits per company.
 */

function makeKeys(entity) {
    return {
        all: [entity],
        lists: () => [entity, 'list'],
        list: (params) => [entity, 'list', params ?? {}],
        infinite: (params) => [entity, 'infinite', params ?? {}],
        details: () => [entity, 'detail'],
        detail: (id, params) => [entity, 'detail', String(id), params ?? {}],
        custom: (key, params) => [entity, 'custom', key, params ?? {}],
    }
}

export const queryKeys = {
    session: {
        me: ['session', 'me'],
        balance: (companyId) => ['session', 'balance', String(companyId ?? '')],
    },
    users: makeKeys('users'),
    companies: makeKeys('companies'),
    postGroups: makeKeys('postGroups'),
    posts: makeKeys('posts'),
    assets: makeKeys('assets'),
    folders: makeKeys('folders'),
    avatars: makeKeys('avatars'),
    voices: makeKeys('voices'),
    tags: makeKeys('tags'),
    comments: makeKeys('comments'),
    internalComments: makeKeys('internalComments'),
    conversations: makeKeys('conversations'),
    messages: makeKeys('messages'),
    socialNetworks: makeKeys('socialNetworks'),
    ruleGroups: makeKeys('ruleGroups'),
    rules: makeKeys('rules'),
    prompts: makeKeys('prompts'),
    banners: makeKeys('banners'),
    bannedDomains: makeKeys('bannedDomains'),
    subscriptions: makeKeys('subscriptions'),
    subscriptionTokens: makeKeys('subscriptionTokens'),
    invoices: makeKeys('invoices'),
    products: makeKeys('products'),
    purchases: makeKeys('purchases'),
    imageCredits: makeKeys('imageCredits'),
    creditTransactions: makeKeys('creditTransactions'),
    aiResults: makeKeys('aiResults'),
    aiResultGroups: makeKeys('aiResultGroups'),
    reactions: makeKeys('reactions'),
    communityPosts: makeKeys('communityPosts'),
    vistaTemplates: makeKeys('vistaTemplates'),
    creative: makeKeys('creative'),
    files: makeKeys('files'),
    externalUsers: makeKeys('externalUsers'),
    whiteLabels: makeKeys('whiteLabels'),
    personalAccessTokens: makeKeys('personalAccessTokens'),
    news: makeKeys('news'),
    googleTrends: makeKeys('googleTrends'),
    twitterTrends: makeKeys('twitterTrends'),
    stockMedia: makeKeys('stockMedia'),
    holidays: makeKeys('holidays'),
    mediumNews: makeKeys('mediumNews'),
    intercomNews: makeKeys('intercomNews'),
    imageCaption: makeKeys('imageCaption'),
    scraper: makeKeys('scraper'),
    proxy: makeKeys('proxy'),
    netlify: makeKeys('netlify'),
    freeTools: makeKeys('freeTools'),
}

/**
 * Entity roots whose data is company-scoped. Used by
 * `invalidateCompanyScopedQueries()` on company switch.
 */
export const COMPANY_SCOPED_ROOTS = [
    'posts',
    'postGroups',
    'assets',
    'folders',
    'avatars',
    'voices',
    'tags',
    'ruleGroups',
    'rules',
    'prompts',
    'comments',
    'internalComments',
    'conversations',
    'messages',
    'subscriptions',
    'subscriptionTokens',
    'invoices',
    'purchases',
    'imageCredits',
    'creditTransactions',
    'aiResults',
    'aiResultGroups',
    'reactions',
    'communityPosts',
    'creative',
    'externalUsers',
    'socialNetworks',
    'whiteLabels',
    'vistaTemplates',
    'banners',
]
